import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";

const defaultWords = [
  "LOADING",
  "METRO CRAFTS",
  "SEARCHING",
  "RETRIEVING",
  "ASSEMBLING",
];

const letterIn = keyframes`
  from {
    transform: perspective(1000px) rotateX(90deg) translateY(20px);
    opacity: 0;
    filter: blur(8px);
  }

  to {
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    opacity: 1;
    filter: blur(0);
  }
`;

const letterOut = keyframes`
  from {
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    opacity: 1;
    filter: blur(0);
  }

  to {
    transform: perspective(1000px) rotateX(-90deg) translateY(-20px);
    opacity: 0;
    filter: blur(8px);
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ $minHeight }) => $minHeight || "200px"};
`;

const Perspective = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
`;

const WordRow = styled.div`
  display: flex;
  gap: 0.1em;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2rem, 5vw, 3.75rem);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ theme, $color }) => $color || theme.colors.offWhite};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: clamp(1.6rem, 8vw, 2.5rem);
  }
`;

const LetterSpan = styled.span`
  display: inline-block;
  transform-style: preserve-3d;
  animation:
    ${letterIn} ${({ $letterDuration }) => $letterDuration}s
      cubic-bezier(0.2, 0.65, 0.3, 0.9) both,
    ${letterOut} ${({ $exitDuration }) => $exitDuration}s ease-in both;
  animation-delay:
    ${({ $index, $staggerDelay }) => $index * $staggerDelay}s,
    ${({ $exitStart, $index, $exitStaggerDelay }) =>
      $exitStart + $index * $exitStaggerDelay}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
    filter: none;
  }
`;

const Letter = memo(function Letter({
  char,
  letterDuration,
  exitDuration,
  index,
  staggerDelay,
  exitStart,
  exitStaggerDelay,
}) {
  return (
    <LetterSpan
      $index={index}
      $letterDuration={letterDuration}
      $exitDuration={exitDuration}
      $staggerDelay={staggerDelay}
      $exitStart={exitStart}
      $exitStaggerDelay={exitStaggerDelay}
    >
      {char}
    </LetterSpan>
  );
});

const Word = memo(function Word({
  text,
  letterDuration,
  staggerDelay,
  exitStaggerDelay,
  textColor,
}) {
  const letters = useMemo(() => text.split(""), [text]);
  const exitDuration = letterDuration * 0.67;
  const enterWindow = Math.max(
    letterDuration,
    (letters.length - 1) * staggerDelay,
  );
  const exitStart = enterWindow + 0.55;

  return (
    <WordRow $color={textColor} aria-hidden="true">
      {letters.map((char, index) => (
        <Letter
          key={`${char}-${index}`}
          char={char}
          index={index}
          letterDuration={letterDuration}
          exitDuration={exitDuration}
          staggerDelay={staggerDelay}
          exitStart={exitStart}
          exitStaggerDelay={exitStaggerDelay}
        />
      ))}
    </WordRow>
  );
});

const FlipFadeText = ({
  words = defaultWords,
  interval = 2500,
  className,
  letterDuration = 0.6,
  staggerDelay = 0.1,
  exitStaggerDelay = 0.05,
  textColor,
  minHeight,
}) => {
  const [index, setIndex] = useState(0);

  const updateIndex = useCallback(() => {
    setIndex((previous) => (previous + 1) % words.length);
  }, [words.length]);

  useEffect(() => {
    const timer = window.setInterval(updateIndex, interval);
    return () => window.clearInterval(timer);
  }, [updateIndex, interval]);

  const currentWord = useMemo(() => words[index], [words, index]);

  return (
    <Wrapper className={className} $minHeight={minHeight}>
      <Perspective
        aria-live="polite"
        aria-atomic="true"
        aria-label={currentWord}
      >
        <Word
          key={currentWord}
          text={currentWord}
          letterDuration={letterDuration}
          staggerDelay={staggerDelay}
          exitStaggerDelay={exitStaggerDelay}
          textColor={textColor}
        />
      </Perspective>
    </Wrapper>
  );
};

export default FlipFadeText;
